import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { DealerRecommendation } from '@quorum/models/xs-misc';
import { ServiceGroup } from '@quorum/models/xs-resource';
import { TwoActionDialogComponent, TwoActionDialogData } from '@quorum/sha-components-ng-ui';
import { forkJoin, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import {
  DealerRecommendationEditDialogComponent,
  DealerRecommendationEditDialogData,
  DealerRecommendationEditDialogResult,
} from './dealer-recommendation-edit-dialog.component';

const DEALER_RECOMMENDATIONS_PATH = 'v/1/fixed-operations/motor/dealer-maintenance-recommendations';

@Component({
  selector: 'svc-dealer-recommendations',
  templateUrl: './dealer-recommendations.component.html',
  styleUrls: ['./dealer-recommendations.component.scss'],
})
export class DealerRecommendationsComponent implements OnInit, OnDestroy {
  recommendations: DealerRecommendation[] = [];
  serviceGroupsById = new Map<string, ServiceGroup>();
  isLoading = true;
  loadError = false;
  saving = false;

  private destroy$ = new Subject<void>();
  private rooftopId: number | null = null;
  private serviceGroups: ServiceGroup[] = [];

  constructor(
    private apiService: ApiService,
    private authenticationStateService: AuthenticationStateService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.authenticationStateService
      .selectAuthenticatedEmployee()
      .pipe(
        filter((emp) => !!emp),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((emp) => {
        this.rooftopId = emp.rooftopId;
        this.loadAll();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAll(): void {
    if (!this.rooftopId) return;
    this.isLoading = true;
    this.loadError = false;

    forkJoin({
      serviceGroups: this.apiService.get('v/1/fixed-operations/service-groups', {
        params: { rooftopId: `${this.rooftopId},-1`, expired: '0', useFlag: 'A,R' },
      }),
      recommendations: this.apiService.get(DEALER_RECOMMENDATIONS_PATH, {
        params: { rooftopId: `${this.rooftopId}` },
      }),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({
          serviceGroups,
          recommendations,
        }: {
          serviceGroups: ServiceGroup[];
          recommendations: DealerRecommendation[];
        }) => {
          this.serviceGroups = serviceGroups;
          this.serviceGroupsById = new Map(serviceGroups.map((sg) => [sg.id, sg]));
          this.recommendations = this.sortRecommendations(recommendations);
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.loadError = true;
        },
      });
  }

  private sortRecommendations(recs: DealerRecommendation[]): DealerRecommendation[] {
    return [...recs].sort((a, b) => {
      const nameA = this.serviceGroupsById.get(a.serviceGroupId)?.name ?? '';
      const nameB = this.serviceGroupsById.get(b.serviceGroupId)?.name ?? '';
      const byName = nameA.localeCompare(nameB);
      if (byName !== 0) return byName;
      const byMileage = (a.intervalMileage ?? 0) - (b.intervalMileage ?? 0);
      if (byMileage !== 0) return byMileage;
      return (a.intervalMonths ?? 0) - (b.intervalMonths ?? 0);
    });
  }

  getServiceGroupName(rec: DealerRecommendation): string {
    return this.serviceGroupsById.get(rec.serviceGroupId)?.name ?? '';
  }

  getServiceGroupOpCode(rec: DealerRecommendation): string {
    return this.serviceGroupsById.get(rec.serviceGroupId)?.workOrderLocationOpCode ?? '';
  }

  isServiceGroupKnown(rec: DealerRecommendation): boolean {
    return this.serviceGroupsById.has(rec.serviceGroupId);
  }

  addRecommendation(): void {
    if (!this.rooftopId || this.saving) return;
    this.openEditDialog({
      mode: 'create',
      recommendation: null,
      serviceGroups: this.serviceGroups,
      rooftopId: this.rooftopId,
    });
  }

  editRecommendation(rec: DealerRecommendation): void {
    if (!this.rooftopId || this.saving || !this.isServiceGroupKnown(rec)) return;
    this.openEditDialog({
      mode: 'edit',
      recommendation: rec,
      serviceGroups: this.serviceGroups,
      rooftopId: this.rooftopId,
    });
  }

  private openEditDialog(data: DealerRecommendationEditDialogData): void {
    const dialogRef = this.dialog.open(DealerRecommendationEditDialogComponent, {
      width: '675px',
      height: '900px',
      maxHeight: '90vh',
      disableClose: true,
      panelClass: 'dealer-rec-edit-dialog-panel',
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: DealerRecommendationEditDialogResult) => {
        if (!result || result.action !== 'save') return;
        if (data.mode === 'create') {
          this.createRecommendation(result.payload);
        } else if (data.recommendation) {
          this.updateRecommendation(data.recommendation, result.payload);
        }
      });
  }

  private createRecommendation(payload: {
    serviceGroupId: string;
    intervalMileage?: number;
    intervalMonths?: number;
  }): void {
    if (!this.rooftopId) return;
    this.saving = true;
    this.apiService
      .post(DEALER_RECOMMENDATIONS_PATH, { rooftopId: this.rooftopId, ...payload })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (created: DealerRecommendation) => {
          this.recommendations = this.sortRecommendations([...this.recommendations, created]);
          this.saving = false;
        },
        error: () => {
          this.saving = false;
          this.showError('Could not save recommendation. Please try again.');
        },
      });
  }

  private updateRecommendation(
    existing: DealerRecommendation,
    payload: { serviceGroupId: string; intervalMileage?: number; intervalMonths?: number }
  ): void {
    if (!this.rooftopId) return;
    this.saving = true;
    this.apiService
      .put(`${DEALER_RECOMMENDATIONS_PATH}/${existing.id}`, payload, {
        params: { rooftopId: `${this.rooftopId}` },
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated: DealerRecommendation) => {
          this.recommendations = this.sortRecommendations(
            this.recommendations.map((r) => (r.id === existing.id ? updated : r))
          );
          this.saving = false;
        },
        error: () => {
          this.saving = false;
          this.showError('Could not save recommendation. Please try again.');
        },
      });
  }

  deleteRecommendation(rec: DealerRecommendation): void {
    if (!this.rooftopId || this.saving) return;
    const name = this.getServiceGroupName(rec);
    const content = name
      ? `Are you sure you want to delete the ${name} recommendation? This cannot be undone.`
      : 'Are you sure you want to delete this recommendation? This cannot be undone.';
    const dialogData: TwoActionDialogData = {
      title: 'Delete Recommendation',
      content,
      actionOneCaption: 'Cancel',
      actionTwoCaption: 'Delete',
    };
    this.dialog
      .open(TwoActionDialogComponent, { width: '400px', disableClose: true, data: dialogData })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.performDelete(rec);
      });
  }

  private performDelete(rec: DealerRecommendation): void {
    if (!this.rooftopId) return;
    this.saving = true;
    this.apiService
      .delete(`${DEALER_RECOMMENDATIONS_PATH}/${rec.id}`, {
        params: { rooftopId: `${this.rooftopId}` },
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.recommendations = this.recommendations.filter((r) => r.id !== rec.id);
          this.saving = false;
        },
        error: () => {
          this.saving = false;
          this.showError('Could not delete recommendation. Please try again.');
        },
      });
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Dismiss', { duration: 5000 });
  }

  trackById(_index: number, rec: DealerRecommendation): string {
    return rec.id;
  }
}
